import { useEffect, useState } from 'react'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { useWorkspace } from 'hooks/useWorkspace'
import { FolderClient } from 'api/clients/FolderClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { LocationPickerFolder, SelectedFolder } from '../LocationPicker'
import { capitalize, truncate } from 'lodash'
import { AlmanacFile } from 'types'
import { ActionType } from '../LocationPickerModal'
import { useBrand } from 'hooks/useBrand'
import { Placement } from '@floating-ui/react-dom'
import { TreeDataItem } from 'components/FilesTree/types'

interface PickerProps {
  folder?: AlmanacFile | null
  onSelectedFolder: (selectedFolder: AlmanacFile) => void
  actionType?: ActionType
  isWorkspaceSelectorEnabled?: boolean
}
interface Props {
  actionType: 'copy' | 'move' | 'create' | 'search'
  initialFolder: LocationPickerFolder | null | undefined
  onSelectFolder: ({
    selectedFolder,
    newWorkspaceId,
  }: {
    selectedFolder: SelectedFolder | null
    newWorkspaceId: number
  }) => void
  initialWorkspaceId?: number
  fileTitle?: string
}

export const useLocationPicker = ({
  actionType,
  initialFolder,
  onSelectFolder,
  initialWorkspaceId,
  fileTitle,
}: Props) => {
  const workspaces = useWorkspaces()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const [workspaceId, setWorkspaceId] = useState(initialWorkspaceId || currentWorkspaceId)
  const [activeFolder, setActiveFolder] = useState<LocationPickerFolder | null | undefined>(initialFolder)
  const actionPreposition = ['create', 'search'].includes(actionType) ? 'in' : 'to'
  const workspace = useWorkspace(workspaceId)

  const [searchInputValue, setSearchInputValue] = useState('')

  useEffect(() => {
    if (!workspace) return

    if (workspace.guest || workspace.id === 1) {
      const validWorkspace = workspaces.filter((w) => ![workspace.id, 1].includes(w.id))[0]?.id
      if (!validWorkspace) return

      setWorkspaceId(validWorkspace)
    }
  }, [workspaceId, workspaces, workspace])

  const onSelect = (folder: SelectedFolder | LocationPickerFolder | null | TreeDataItem) => {
    if (!workspaceId) return

    const seo_string = (folder as SelectedFolder)?.seo_string || (folder as TreeDataItem)?.handbook?.seo_string
    const handbook_key = (folder as SelectedFolder)?.handbook_key || (folder as TreeDataItem)?.handbook?.key

    const selectedFolder = {
      id: folder?.id,
      title: folder?.title,
      seo_string: seo_string,
      ancestor_projects: folder?.ancestor_projects,
      handbook_key: handbook_key,
      default_view: folder?.default_view,
      database_mode: folder?.database_mode,
      header_emoji: folder?.header_emoji,
      project_id: folder?.project_id,
    } as SelectedFolder

    onSelectFolder({ newWorkspaceId: workspaceId, selectedFolder })
    setActiveFolder({
      ...folder,
      ancestor_keys: folder?.ancestor_projects?.map((p) => p.id) || [],
      project_id: folder?.project_id,
    } as LocationPickerFolder)
  }

  const handleHitClick = async (hit: { key: string; title: string }) => {
    const res = await FolderClient.getFolder(hit.key)

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      setSearchInputValue('')
      const { folder } = res.data
      const { handbook } = folder

      if (!handbook) return

      onSelect({
        id: folder.id,
        title: folder.title,
        seo_string: handbook.seo_string,
        handbook_key: handbook.key,
        default_view: folder.default_view,
        database_mode: folder.database_mode,
        header_emoji: folder.header_emoji,
        project_id: folder.project_id,
      })
      return
    }
    showToastMessage(`Unable to ${actionType} ${actionPreposition} folder: ${hit.title}`, 'error')
  }

  const searchPlaceholder = `${capitalize(actionType)}${
    fileTitle ? ` ${truncate(fileTitle, { length: 20 })}` : ''
  } ${actionPreposition}`

  return {
    handleHitClick,
    onSelect,
    searchInputValue,
    setSearchInputValue,
    activeFolder,
    setActiveFolder,
    searchPlaceholder,
  }
}

export const useLocationPickerProps = ({
  folder,
  onSelectedFolder,
  actionType = 'create',
  isWorkspaceSelectorEnabled = false,
}: PickerProps) => {
  const { defaultFoldersLocation } = useBrand()

  return {
    buttonLabel: folder?.title || defaultFoldersLocation,
    dropdownTriggerProps: { placement: 'bottom-start' as Placement, offsetOptions: { mainAxis: -40, crossAxis: 0 } },
    locationPickerProps: {
      actionType,
      isRootDisabled: true,
      isWorkspaceSelectorEnabled,
      initialFolder: folder
        ? {
            ...folder,
            ancestor_keys: folder.ancestor_projects.map((p) => p.id),
          }
        : null,
      onSelectFolder: async ({ selectedFolder }: { selectedFolder: SelectedFolder | null }) => {
        let newSelectedFolder = null
        if (selectedFolder) {
          const response = await FolderClient.getFolder(selectedFolder.id)

          if (response.type === SUCCESS_RESPONSE_TYPE) {
            newSelectedFolder = response.data.folder
          }
        }
        if (newSelectedFolder) {
          onSelectedFolder(newSelectedFolder)
        }
      },
    },
  }
}
